import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { DBTable } from '../components/DBTable';
import { supabase } from '../lib/supabase';
import { useDataTable } from '../hooks/useDataTable';
import { usePermission } from '../hooks/usePermission';
import { AccessLevel } from '../types';
import { 
  X, 
  Save, 
  Plus,
  ChevronLeft, 
  ArrowLeft,
  Database, 
  AlertCircle,
  Clock,
  User as UserIcon,
  Search,
  RefreshCw,
  Hash,
  Type,
  Calendar,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

interface ColumnInfo {
  column_name: string;
  data_type: string;
  is_nullable: string;
  column_default: any;
}

const SysTableEdit: React.FC = () => {
  const { tableName } = useParams<{ tableName: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const [columns, setColumns] = useState<ColumnInfo[]>([]);
  const [loadingSchema, setLoadingSchema] = useState(true);

  const hasAccess = hasPermission('app.sistema.sysedit', AccessLevel.ro);

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center bg-white rounded-3xl border border-slate-100 shadow-xs max-w-2xl mx-auto my-12">
        <div id="no-access-table-icon-wrapper" className="w-16 h-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mb-4 border border-red-100/50">
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 15v2m0-6v4m0-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 className="text-xl font-black text-slate-800 uppercase tracking-tight">Accesso non consentito</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-sm font-semibold">
          Non hai i permessi necessari per accedere a questa sezione. È richiesto l'inserimento nel gruppo <code className="bg-slate-50 border border-slate-100 px-1.5 py-0.5 rounded-lg font-mono text-xs text-red-600 font-bold">app.sistema.sysedit</code>.
        </p>
      </div>
    );
  }
  
  const { insertRow, updateRow } = useDataTable({ tableName: tableName || '', adminMode: true });
  
  const [editingRow, setEditingRow] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchSchema = async () => {
      if (!tableName) return;
      setLoadingSchema(true);
      try {
        // 1. Proviamo tramite l'endpoint server (che usa la Service Key e RPC)
        const resp = await fetch(`/api/sys/columns/${tableName}`);
        if (resp.ok) {
          const data = await resp.json();
          if (data && Array.isArray(data) && data.length > 0) {
            setColumns(data);
            return;
          }
        }

        // 2. Fallback extreme: query diretta dal client (se l'utente ha permessi)
        const { data: directData } = await supabase
          .schema('information_schema')
          .from('columns')
          .select('column_name, data_type, is_nullable, column_default')
          .eq('table_name', tableName)
          .eq('table_schema', 'public');

        if (directData && directData.length > 0) {
          setColumns(directData);
          return;
        }

        // 3. Fallback per inferenza se nulla funziona
        const { data: sampleData } = await supabase
          .from(tableName)
          .select('*')
          .limit(1);
        
        if (sampleData && sampleData.length > 0) {
          setColumns(Object.keys(sampleData[0]).map(col => ({
            column_name: col,
            data_type: typeof sampleData[0][col] === 'number' ? 'numeric' : 
                       typeof sampleData[0][col] === 'boolean' ? 'boolean' : 'text',
            is_nullable: 'YES',
            column_default: null
          })));
        } else {
           toast.error("Impossibile recuperare lo schema preciso. L'editor potrebbe essere limitato.");
        }
      } catch (err) {
        console.error('Error fetching schema:', err);
      } finally {
        setLoadingSchema(false);
      }
    };

    fetchSchema();
  }, [tableName]);

  const handleEdit = (row: any) => {
    setEditingRow(row);
    setFormData({ ...row });
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingRow(null);
    const initialData: Record<string, any> = {};
    columns.forEach(col => {
      if (col.column_name === 'id') return;
      initialData[col.column_name] = '';
    });
    setFormData(initialData);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!tableName) return;
    setIsSaving(true);
    try {
      if (editingRow) {
        await updateRow(editingRow.id || editingRow.table_name || editingRow.item, formData);
        toast.success('Riferimento aggiornato con successo');
      } else {
        await insertRow(formData);
        toast.success('Record inserito con successo');
      }
      setIsModalOpen(false);
    } catch (err: any) {
      console.error('Save error:', err);
      toast.error(`Errore durante il salvataggio: ${err.message || 'Errore sconosciuto'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const renderInput = (col: ColumnInfo) => {
    const name = col.column_name;
    const type = col.data_type.toLowerCase();
    const value = formData[name] ?? '';

    // Campi gestiti automaticamente o immutabili
    const isSystemField = ['created_at', 'updated_at', 'inserito_da', 'modificato_da', 'data_inserimento', 'data_aggiornamento'].includes(name);

    if (name === 'id' && editingRow) {
      return (
        <div className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-500 text-sm font-mono italic">
          {value} (Auto-generato)
        </div>
      );
    }

    if (type.includes('json') || typeof value === 'object') {
      return (
        <textarea
          value={typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
          onChange={(e) => {
            try {
              const val = e.target.value.trim() ? JSON.parse(e.target.value) : null;
              setFormData({ ...formData, [name]: val });
            } catch (err) {
              setFormData({ ...formData, [name]: e.target.value });
            }
          }}
          className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary font-mono text-xs min-h-[100px]"
          placeholder="{}"
        />
      );
    }

    if (type.includes('bool')) {
      return (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setFormData({ ...formData, [name]: !value })}
            className={cn(
              "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20",
              value ? "bg-primary" : "bg-slate-200"
            )}
          >
            <span className={cn(
              "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
              value ? "translate-x-6" : "translate-x-1"
            )} />
          </button>
          <span className="text-sm text-slate-600">{value ? 'Sì' : 'No'}</span>
        </div>
      );
    }

    if (type.includes('date') || type.includes('timestamp')) {
      return (
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type={type.includes('timestamp') ? "datetime-local" : "date"}
            value={value ? (type.includes('timestamp') ? value.substring(0, 16) : value.substring(0, 10)) : ''}
            onChange={(e) => setFormData({ ...formData, [name]: e.target.value })}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
          />
        </div>
      );
    }

    if (type.includes('int') || type.includes('numeric') || type.includes('double')) {
      return (
        <div className="relative">
          <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="number"
            step="any"
            value={value}
            onChange={(e) => setFormData({ ...formData, [name]: e.target.value === '' ? null : Number(e.target.value) })}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm font-mono"
          />
        </div>
      );
    }

    return (
      <div className="relative">
        <Type className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          value={value || ''}
          onChange={(e) => setFormData({ ...formData, [name]: e.target.value })}
          className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
          disabled={isSystemField}
          placeholder={isSystemField ? "(Gestito dal sistema)" : ""}
        />
      </div>
    );
  };

  if (!tableName) return <Navigate to="/sistema/sysedit" />;

  return (
    <div className="container mx-auto pb-8 pt-0 px-4 space-y-6">
      {loadingSchema ? (
        <div className="p-24 bg-white rounded-3xl border border-slate-200 animate-pulse flex flex-col items-center justify-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
          <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Analisi struttura in corso...</p>
        </div>
      ) : (
        <div className="-mt-2">
          <DBTable 
            tableName={tableName}
            columns={columns.map(c => c.column_name)}
            title={
              <div className="flex items-center gap-1.5 -ml-2 text-xs">
                <button 
                  onClick={() => navigate('/sistema/sysedit')}
                  className="p-1 hover:bg-black/5 rounded-full transition-colors text-slate-500 hover:text-slate-900"
                  title="Torna alla lista tabelle"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                </button>
                <Database className="w-3.5 h-3.5 text-slate-400 ml-1 shrink-0" />
                <span className="font-bold">Editor:</span>
                <span className="font-mono text-primary font-bold truncate">{tableName}</span>
              </div>
            }
            icon={null}
            sortable={true}
            searchable={true}
            filterable={true}
            selectable={true}
            onEdit={handleEdit}
            onAdd={handleAdd}
            pinnedColumns={1}
            pageSize={1000}
            adminMode={true}
          />
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] scale-in-center">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white shadow-sm border border-slate-100 rounded-xl">
                  {editingRow ? <Save className="w-5 h-5 text-primary" /> : <Plus className="w-5 h-5 text-primary" />}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-800 leading-none">
                    {editingRow ? 'Modifica Record' : 'Nuovo Record'}
                  </h3>
                  <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider font-bold">In {tableName}</p>
                </div>
              </div>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-8 space-y-6 overflow-y-auto flex-1 custom-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(columns.length > 0 ? columns : Object.keys(formData).map(k => ({ column_name: k, data_type: 'text' } as any))).map((col: ColumnInfo) => (
                  <div key={col.column_name} className={cn(
                    "space-y-1.5",
                    (col.data_type.includes('json') || col.column_name === 'note') && "md:col-span-2"
                  )}>
                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                       {col.column_name}
                       {col.is_nullable === 'NO' && col.column_name !== 'id' && <span className="text-red-500">*</span>}
                    </label>
                    {renderInput(col)}
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-6 py-2.5 text-sm font-bold text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-2xl transition-all"
              >
                ANNULLA
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-8 py-2.5 text-sm font-black bg-primary text-white rounded-2xl hover:bg-primary/90 transition-all shadow-lg hover:shadow-primary/20 flex items-center gap-2 disabled:opacity-50"
              >
                {isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {editingRow ? 'SALVA MODIFICHE' : 'INSERISCI RECORD'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SysTableEdit;
